import React, { useContext, useEffect, useRef } from 'react';
import delay from 'lodash/delay';
import { Box, xcss } from '@atlaskit/primitives';
import { AnnouncerV2 } from '@atlassian/jira-accessibility/src/ui/announcer-v2/index.tsx';
import ProjectSettingsBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project-settings/index.tsx';
import ProjectBreadcrumb from '@atlassian/jira-breadcrumbs/src/common-breadcrumbs/project/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	FireTrackAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import SetCustomMark from '@atlassian/jira-providers-spa-apdex-analytics/src/set-custom-mark/index.tsx';
import SubmitApdex from '@atlassian/jira-providers-spa-apdex-analytics/src/submit-apdex-mark/index.tsx';
import { SOFTWARE } from '@atlassian/jira-shared-types/src/application-key.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import UFOLoadHold from '@atlassian/react-ufo/load-hold';
import { ANALYTICS_ACCESS_SOURCE, PEOPLE_PAGE_APP_NAME, READY_MARK_KEY } from '../common/constants';
import type { AccessPageContext } from '../common/types';
import FilterControllerDI from '../controllers/filter-controller/main.tsx';
import { ReloadPageProvider } from '../controllers/reload-page-context';
import { TableResetter } from '../controllers/table-filter-meta-controller';
import MainProviderContext from '../services/access-page-context';
import ActorsServiceDI from '../services/actors-api';
import CurrentAccessServiceDI from '../services/current-access-service';
import RoleServiceDI from '../services/roles-api';
import { getDefaultRoleId, getRoleFromDetails } from '../services/roles-api/utils';
import { getMergedActorsAndRolesApi, refreshActorsAndRolesApi } from '../services/utils';
import { projectSettingsAccessPagePageLoad } from '../utils/performance-analytics';
import ActorsTable from './actors-table';
import Header from './header';
import HeaderFormContainer from './header-form-container';
import messages from './messages';
import ProjectAccessModalViewWrapper from './project-access-modal-view-wrapper/async';
import RolesFilterContainer from './roles-filter-container';
import TextFilterContainer from './text-filter';
import type { Props } from './types';

let isFirstTimeCompleted = false;

const isInitialDataLoadCompleted = (isActorFetching: boolean): boolean => {
	if (!isFirstTimeCompleted && !isActorFetching) {
		isFirstTimeCompleted = true;
	}
	return isFirstTimeCompleted;
};

const renderSPAApdexAnalytics = (isActorFetching: boolean) => {
	if (isInitialDataLoadCompleted(isActorFetching)) {
		// Only want to send SetCustomMark and SubmitApdex for the first time when page is loaded
		// and api response of actors received

		return (
			<>
				<SetCustomMark name={READY_MARK_KEY} />
				<SubmitApdex
					appName={PEOPLE_PAGE_APP_NAME}
					// @ts-expect-error - TS2769 - No overload matches this call.
					metric={projectSettingsAccessPagePageLoad}
				/>
			</>
		);
	}
	return <UFOLoadHold name="loading" />;
};

export const RenderApp = (props: Props) => {
	const { showBreadcrumbs, RoleService, ActorsService, FilterController, CurrentAccessService } =
		props;
	const { projectType, isProjectSimplified, projectName }: AccessPageContext =
		useContext(MainProviderContext);
	let accessFetchStarted = false;
	let refetchTimeout: number;
	const componentMountTracker = useRef<boolean>(true);

	const { formatMessage } = useIntl();

	useEffect(
		() => () => {
			// Resetting it as  for functional component this will act as component will unmount
			componentMountTracker.current = false;
			isFirstTimeCompleted = false;
		},
		[],
	);

	return (
		<>
			<RoleService>
				{(rolesApiData) => {
					const defaultRoleId: number = getDefaultRoleId(rolesApiData.data);
					const rolesApiCall = {
						loading: rolesApiData.loading,
						data: rolesApiData.data,
						error: rolesApiData.error,
					};
					return (
						<CurrentAccessService>
							{({
								data: accessType,
								fetch: accessFetch,
								loading: accessLoading,
								error: accessError,
							}) => {
								if (
									!accessLoading &&
									!accessType &&
									isProjectSimplified &&
									!accessFetchStarted &&
									!accessError
								) {
									accessFetch();
									accessFetchStarted = true;
								}
								return (
									<FilterController>
										{({ filters, defaultFilters, onTextChange, onRoleChange, onTableChange }) => (
											<ActorsService filters={filters}>
												{(actorsCall) => {
													const mergedResponse = getMergedActorsAndRolesApi(
														actorsCall,
														rolesApiCall,
													);
													const onRefresh = refreshActorsAndRolesApi(actorsCall, rolesApiData);
													const isFetching = rolesApiData.loading || actorsCall.loading;
													const tableRefetch = () => {
														// this delay prevents UI from jumping and
														// making sure when we refetch we have data available
														// Useref should be removed once this PR is merged and react-hooks version bump is done
														// https://github.com/apollographql/apollo-client/pull/6314
														clearTimeout(refetchTimeout);
														refetchTimeout = delay(() => {
															if (componentMountTracker && componentMountTracker.current) {
																actorsCall && actorsCall.refetch();
															}
														}, 2000);
													};

													const shouldShowSkeleton = !isInitialDataLoadCompleted(isFetching);
													const isLastPage = mergedResponse ? mergedResponse.isLastBatch : false;
													return (
														<ReloadPageProvider actorsApi={actorsCall} rolesApi={rolesApiData}>
															<Header
																showBreadcrumbs={showBreadcrumbs}
																isError={!!rolesApiData.error}
																isFetching={rolesApiData.loading}
																defaultRoleId={defaultRoleId}
																projectRoles={rolesApiData.data || []}
																isProjectSimplified={isProjectSimplified}
																accessType={accessType}
																onAddActorSuccess={tableRefetch}
															/>
															{isProjectSimplified ? (
																// @ts-expect-error - TS2741 - Property 'UpdateAccessTypeService' is missing in type '{ accessType: AccessType | null; onFetchAccessType: () => Promise<AccessType | null>; projectName: string; projectType: ProjectType; fetchAccessError: FetchError | null; }' but required in type 'Props'.
																<ProjectAccessModalViewWrapper
																	accessType={accessType}
																	onFetchAccessType={accessFetch}
																	projectName={projectName}
																	projectType={projectType}
																	fetchAccessError={accessError}
																/>
															) : null}
															<section data-testid="users-and-roles.ui.header-form">
																<HeaderFormContainer>
																	<TextFilterContainer
																		searchText={filters.searchText || ''}
																		onTextChange={onTextChange}
																		isFetching={shouldShowSkeleton}
																	/>
																	<RolesFilterContainer
																		roles={rolesApiCall}
																		rolesFilter={{
																			roleIds: filters.roleIds,
																		}}
																		onRoleChange={onRoleChange}
																		isFetching={shouldShowSkeleton}
																	/>
																</HeaderFormContainer>
															</section>
															<TableResetter defaultFilters={defaultFilters} filters={filters}>
																<ActorsTable
																	actors={mergedResponse ? mergedResponse.actors : []}
																	accessType={accessType}
																	tableFilters={{
																		orderField: filters.orderField,
																		orderDirection: filters.orderDirection,
																		pageNumber: filters.pageNumber,
																		pageSize: filters.pageSize,
																	}}
																	onTableChange={onTableChange}
																	onRefresh={onRefresh}
																	projectRoles={getRoleFromDetails(rolesApiData.data || [])}
																	isError={
																		isFetching ? false : !!(rolesApiData.error || actorsCall.error)
																	}
																	isFetching={isFetching}
																	shouldShowSkeleton={shouldShowSkeleton}
																	onDataChange={actorsCall.onDataChange}
																	isLastPage={isLastPage}
																/>
															</TableResetter>

															<AnnouncerV2
																message={formatMessage(
																	isLastPage
																		? messages.screenReaderSearchResult
																		: messages.screenReaderSearchResultMoreThanOnePage,
																	{
																		count: mergedResponse?.actors?.length || 0,
																	},
																)}
																shouldAnnounce={!isFetching}
															/>

															{renderSPAApdexAnalytics(actorsCall.loading)}
														</ReloadPageProvider>
													);
												}}
											</ActorsService>
										)}
									</FilterController>
								);
							}}
						</CurrentAccessService>
					);
				}}
			</RoleService>
			<FireTrackAnalytics eventName="project viewed" />
			<FireScreenAnalytics />
		</>
	);
};

RenderApp.defaultProps = {
	RoleService: RoleServiceDI,
	ActorsService: ActorsServiceDI,
	FilterController: FilterControllerDI,
	CurrentAccessService: CurrentAccessServiceDI,
};

export const UsersAndRoles = (props: Props) => {
	const { showBreadcrumbs } = props;

	const {
		baseUrl,
		projectId,
		projectKey,
		projectType,
		isProjectSimplified,
		projectName,
	}: AccessPageContext = useContext(MainProviderContext);
	const { productEntitlementDetails } = useTenantContext();
	const billingSourceSystem =
		productEntitlementDetails?.[SOFTWARE]?.billingSourceSystem === 'CCP' ? 'CCP' : 'HAMS';
	const analyticsSource = ANALYTICS_ACCESS_SOURCE;

	return (
		<Box xcss={fullWidthWrapperStyles}>
			<ContextualAnalyticsData
				sourceName={analyticsSource}
				sourceType={SCREEN}
				containerType="project"
				containerId={`${projectId}`}
				attributes={{
					billingSourceSystem,
					isProjectSimplified,
					projectType,
					projectId,
				}}
			>
				{showBreadcrumbs ? (
					<ProjectBreadcrumb baseUrl={baseUrl} projectKey={projectKey} projectName={projectName}>
						<ProjectSettingsBreadcrumb
							baseUrl={baseUrl}
							projectKey={projectKey}
							projectType={projectType}
						>
							<RenderApp {...props} />
						</ProjectSettingsBreadcrumb>
					</ProjectBreadcrumb>
				) : (
					<>
						<RenderApp {...props} />
					</>
				)}
			</ContextualAnalyticsData>
		</Box>
	);
};

UsersAndRoles.defaultProps = {
	RoleService: RoleServiceDI,
	ActorsService: ActorsServiceDI,
	FilterController: FilterControllerDI,
	CurrentAccessService: CurrentAccessServiceDI,
};

export default UsersAndRoles;

const fullWidthWrapperStyles = xcss({
	width: '100%',
});
